import React, { useRef } from "react";
import { ImagePlus, Plus } from "lucide-react";
import { PresignedImage } from "@/components/common/PresignedImage";
import { Button } from "@/components/ui/button";

interface GeneralInfoLogoUploaderProps {
  alt: string;
  changeLabel: string;
  fileId?: string;
  previewUrl?: string;
  onChange: (file: File) => void;
}

export const GeneralInfoLogoUploader = ({
  alt,
  changeLabel,
  fileId,
  previewUrl,
  onChange,
}: GeneralInfoLogoUploaderProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      onChange(file);
    }

    event.target.value = "";
  };

  const handleOpenFilePicker = () => {
    inputRef.current?.click();
  };

  let logoContent: React.ReactNode = (
    <ImagePlus className="text-muted-foreground size-8" />
  );

  if (fileId) {
    logoContent = (
      <PresignedImage
        fileId={fileId}
        alt={alt}
        className="size-full object-cover"
      />
    );
  }

  if (previewUrl) {
    logoContent = (
      <img src={previewUrl} alt={alt} className="size-full object-cover" />
    );
  }

  return (
    <div className="relative">
      <div className="bg-muted flex size-28 items-center justify-center overflow-hidden rounded-2xl border shadow-sm">
        {logoContent}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleFileChange}
        tabIndex={-1}
      />

      <Button
        type="button"
        variant="new_outline"
        size="icon"
        className="bg-card absolute -right-2 -bottom-2 rounded-full"
        aria-label={changeLabel}
        onClick={handleOpenFilePicker}
      >
        <Plus />
      </Button>
    </div>
  );
};
